/**
 * InterviewRoundsList Component
 * 
 * Drag-and-drop list of interview rounds with default/custom sections
 */

import React, { useMemo } from 'react';
import { Empty, Typography } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import InterviewRoundCard from './InterviewRoundCard';
import AddRoundCard from './AddRoundCard';
import { InterviewRoundState } from '../utils/formTransformers';

const { Title } = Typography;

interface InterviewRoundsListProps {
  rounds: InterviewRoundState[];
  onReorder: (newRounds: InterviewRoundState[]) => void;
  onEdit: (round: InterviewRoundState) => void;
  onDelete: (roundId: string) => void;
  onAddRound: () => void;
  isEditMode?: boolean;
  isProfileMatchRequired?: boolean;
  onProfileMatchRequiredChange?: (checked: boolean) => void;
}

/**
 * Sortable Interview Round Item
 */
const SortableRoundItem: React.FC<{
  round: InterviewRoundState;
  index: number;
  isProfileMatching: boolean;
  onEdit: (round: InterviewRoundState) => void;
  onDelete: (roundId: string) => void;
  isEditMode?: boolean;
}> = ({ round, index, isProfileMatching, onEdit, onDelete, isEditMode = false }) => {
  // Generate a stable ID for sorting - use _id or interviewRoundId (stable identifiers)
  // Only use fallback if neither exists (shouldn't happen for rounds with IDs)
  const sortableId = round._id || round.interviewRoundId || `round-temp-${index}`;
  
  // In edit mode, disable drag for existing rounds (only allow for newly added rounds)
  // This prevents breaking candidate interview progression
  const isDragDisabled = isEditMode && !round._isNewlyAdded;
  
  // Call hook unconditionally (React Hooks rules)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: sortableId,
    disabled: isDragDisabled, // Disable sortable for existing rounds in edit mode
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <InterviewRoundCard
        round={round}
        index={index}
        isProfileMatching={isProfileMatching}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={isProfileMatching || isDragDisabled ? undefined : { ...attributes, ...listeners }}
        isDragging={isDragging}
        isEditMode={isEditMode}
        isDragDisabled={isDragDisabled}
      />
    </div>
  );
};

const InterviewRoundsList: React.FC<InterviewRoundsListProps> = ({
  rounds,
  onReorder,
  onEdit,
  onDelete,
  onAddRound,
  isEditMode = false,
  isProfileMatchRequired = true,
  onProfileMatchRequiredChange,
}) => {
  // Configure sensors for drag and drop
  // Use activation constraint to allow button clicks to work
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag activates (allows clicks on buttons)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get all rounds in order, sorted by round number
  const sortedRounds = useMemo(() => {
    return [...rounds].sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber);
  }, [rounds]);

  // Identify Profile Matching round (fixed at position 1 - cannot be dragged/edited/deleted)
  const isProfileMatchingRound = (round: InterviewRoundState) => {
    return round.interviewRoundNumber === 1 || 
           round.title?.toLowerCase() === 'profile matching' ||
           round.interviewRoundId === 'profile-match' ||
           round._id === 'profile-match';
  };

  // Get draggable rounds (all except Profile Matching)
  // Overall Assessment and Custom rounds can be shuffled
  const draggableRounds = useMemo(() => {
    return sortedRounds.filter(round => 
      !isProfileMatchingRound(round)
    );
  }, [sortedRounds]);

  // Handle drag end for all draggable rounds
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Create a map of IDs to round indices for reliable lookup
      // Match by _id, interviewRoundId, or fallback ID (matching useSortable logic)
      const idToIndexMap = new Map<string, number>();
      for (let idx = 0; idx < draggableRounds.length; idx++) {
        const round = draggableRounds[idx];
        const primaryId = round._id;
        const secondaryId = round.interviewRoundId;
        const fallbackId = `round-temp-${idx}`;
        
        // Map all possible IDs to the index
        if (primaryId) idToIndexMap.set(primaryId, idx);
        if (secondaryId) idToIndexMap.set(secondaryId, idx);
        idToIndexMap.set(fallbackId, idx);
      }
      
      const oldIndex = idToIndexMap.get(active.id as string);
      const newIndex = idToIndexMap.get(over.id as string);

      if (oldIndex !== undefined && newIndex !== undefined && oldIndex !== newIndex) {
        // In edit mode, check if this reorder would move newly added rounds before existing rounds
        // Newly added rounds can only be reordered among themselves (after all existing rounds)
        if (isEditMode) {
          // Find the highest index of existing (non-newly-added) rounds in draggableRounds
          const existingRoundIndices = draggableRounds
            .map((r, idx) => ({ round: r, idx }))
            .filter(({ round }) => !round._isNewlyAdded)
            .map(({ idx }) => idx);
          
          const maxExistingIdx = existingRoundIndices.length > 0 
            ? Math.max(...existingRoundIndices) 
            : -1;
          
          // Check if we're trying to move a newly added round before/between existing rounds
          const draggedRound = draggableRounds[oldIndex];
          if (draggedRound._isNewlyAdded && existingRoundIndices.length > 0 && newIndex <= maxExistingIdx) {
            // Block this drop - newly added rounds cannot be placed before existing rounds
            return;
          }
        }
        
        // Create a copy to avoid mutating the original array
        const reorderedRounds = [...draggableRounds];
        const [movedRound] = reorderedRounds.splice(oldIndex, 1);
        reorderedRounds.splice(newIndex, 0, movedRound);
        
        // Find Profile Matching round (fixed at position 1) - identify by ID/title, not round number
        const profileMatchingRound = sortedRounds.find(r => 
          r.title?.toLowerCase() === 'profile matching' ||
          r.interviewRoundId === 'profile-match' ||
          r._id === 'profile-match'
        );
        
        // Update round numbers for all draggable rounds (starting from 2, after Profile Matching (1))
        // Preserve all round data to avoid losing Overall Assessment or any custom round data
        const updatedDraggableRounds = reorderedRounds.map((round, index) => ({
          ...round, // CRITICAL: Preserve ALL properties (title, description, prompts, _id, interviewRoundId, etc.)
          interviewRoundNumber: index + 2, // Start from 2 (after Profile Matching (1))
        }));

        // Combine rounds in order: Profile Matching (1) -> Draggable rounds
        const allRounds: InterviewRoundState[] = [];
        
        // Add Profile Matching at position 1
        if (profileMatchingRound) {
          allRounds.push({ 
            ...profileMatchingRound, 
            interviewRoundNumber: 1,
            isMustQualify: profileMatchingRound.isMustQualify ?? true,
          });
        }
        
        // Add all draggable rounds (Overall Assessment and Custom rounds)
        allRounds.push(...updatedDraggableRounds);

        // Final re-numbering to ensure sequential order
        const finalRounds = allRounds.map((round, index) => ({
          ...round,
          interviewRoundNumber: index + 1,
        }));

        onReorder(finalRounds);
      }
    }
  };

  // Generate consistent IDs for draggable rounds (must match the IDs used in useSortable)
  const draggableRoundIds = useMemo(() => {
    return draggableRounds.map((round, index) => 
      round._id || round.interviewRoundId || `round-temp-${index}`
    );
  }, [draggableRounds]);

  // Show empty state if no rounds at all
  if (rounds.length === 0) {
    return (
      <Empty
        description={
          <Typography.Text type="secondary">
            No assessments added yet. Default rounds will be added automatically.
          </Typography.Text>
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div className="interview-rounds-list">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={draggableRoundIds}
          strategy={verticalListSortingStrategy}
        >
          {sortedRounds.map((round, index) => {
            const isProfileMatching = round.interviewRoundNumber === 1 || 
                                      round.title?.toLowerCase() === 'profile matching' ||
                                      round.interviewRoundId === 'profile-match' ||
                                      round._id === 'profile-match';
            
            // For Profile Matching, render as regular card (not sortable)
            // But show drag icon (disabled) for uniform structure
            if (isProfileMatching) {
              return (
                <div key={round.interviewRoundId || round._id || `round-${round.interviewRoundNumber}`}>
                  <InterviewRoundCard
                    round={round}
                    index={index}
                    isProfileMatching={isProfileMatching}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    dragHandleProps={undefined} // No functional drag handle, but icon will be shown as disabled
                    isDragging={false}
                    isEditMode={isEditMode}
                    isProfileMatchRequired={isProfileMatchRequired}
                    onProfileMatchRequiredChange={onProfileMatchRequiredChange}
                    isProfileMatchRequiredDisabled={isEditMode}
                  />
                </div>
              );
            }
            
            // For draggable rounds (Overall Assessment and Custom rounds), render as sortable items
            // Use the index within draggableRounds for consistent ID generation
            const draggableIndex = draggableRounds.findIndex(r => 
              (r._id && r._id === round._id) ||
              (r.interviewRoundId && r.interviewRoundId === round.interviewRoundId) ||
              (r.interviewRoundNumber === round.interviewRoundNumber && r.title === round.title)
            );
            
            return (
              <SortableRoundItem
                key={round._id || round.interviewRoundId || `round-${round.interviewRoundNumber}-${draggableIndex}`}
                round={round}
                index={draggableIndex >= 0 ? draggableIndex : index}
                isProfileMatching={false}
                onEdit={onEdit}
                onDelete={onDelete}
                isEditMode={isEditMode}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <AddRoundCard onClick={onAddRound} />
    </div>
  );
};

export default InterviewRoundsList;
